// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ProductAddToListLink from "@insite/content-library/Components/ProductAddToListLink";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CommonProductCardProps, withProductCardCommonProps } from "@werner/App/Components/Product/common";
import ProductActions, { ProductActionsStyle } from "@werner/App/Components/Product/ProductActions";
import ProductGridContainer, { ProductGridContainerStyle } from "@werner/App/Components/Product/ProductGridContainer";
import { mergeWithCss, sharedProductDescriptionStyles } from "@werner/XDL/Utilities";
import React from "react";
import { useState } from "react";
import { css } from "styled-components";

export interface ProductMiniCardStyles {
    productGridContainerStyles?: ProductGridContainerStyle;
    imageAndDescriptionSection?: InjectableCss;
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    productActions?: ProductActionsStyle;
}

const productMiniCardStyles: ProductMiniCardStyles = {
    productGridContainerStyles: {
        container: {
            css: css`
                max-width: 416px;
            `,
        },
        info: {
            css: css`
                padding: 0 0 10px;
                .product-description {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
            `,
        },
        image: {
            css: css`
                width: 100px;
                height: 100px;
                padding: 12px;
            `,
        },
    },
    imageAndDescriptionSection: {
        css: css`
            display: flex;
            gap: 20px;
        `,
    },
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 75px;
                height: 75px;
            `,
        },
        img: {
            css: css`
                max-width: 100%;
                max-height: 100%;
                height: auto;
            `,
        },
    },
    productDescription: {
        ...sharedProductDescriptionStyles,
    },
    productActions: {
        productQuantityOrderedStyles: {
            cssOverrides: {
                inputSelect: css`
                    width: 100px;
                `,
            },
        },
        addToCartButtonWrapper: {
            css: css`
                flex-grow: 1;
            `,
        },
    },
};

type Props = CommonProductCardProps<ProductMiniCardStyles>;

const ProductMiniCard = ({
    productContext,
    showPrice,
    showAddToList,
    showAddToCart,
    productSettings,
    extendedStyles,
}: Props) => {
    const [styles] = useState(() => mergeWithCss(productMiniCardStyles, extendedStyles));
    return (
        <ProductGridContainer extendedStyles={styles.productGridContainerStyles}>
            <ProductGridContainer.Info>
                <StyledWrapper {...styles.imageAndDescriptionSection}>
                    <ProductGridContainer.Image>
                        <ProductImage
                            product={productContext}
                            showSmallImage={true}
                            extendedStyles={styles.productImage}
                        />
                    </ProductGridContainer.Image>
                    <StyledWrapper className="product-description">
                        <ProductDescription product={productContext} extendedStyles={styles.productDescription} />
                        {showPrice && productSettings.canSeePrices && (
                            <ProductPrice
                                product={productContext}
                                showLabel={false}
                                showSavings={true}
                                showSavingsAmount={productSettings.showSavingsAmount}
                                showSavingsPercent={productSettings.showSavingsPercent}
                            />
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </ProductGridContainer.Info>
            <ProductGridContainer.AddToCartActions>
                {showAddToCart && <ProductActions extendedStyles={styles.productActions} />}
            </ProductGridContainer.AddToCartActions>
            <ProductGridContainer.AddToList>{showAddToList && <ProductAddToListLink />}</ProductGridContainer.AddToList>
        </ProductGridContainer>
    );
};

export default withProductCardCommonProps(ProductMiniCard);
